import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import atatus from 'atatus';

import { AppRoutes } from 'routes';

import { getAccounts } from '../services/AccountService';

import useAuth from './useAuth';

const Context = createContext();

export const AccountsProvider = ({ children }) => {
    const { signOut, user } = useAuth();
    const [isFetchingAccounts, setAccountFetchingStatus] = useState(false);
    const [accounts, setAccounts] = useState(null);
    const [activeAccountIndex, setActiveAccountIndex] = useState(0);

    const activeAccount = useMemo(() => {
        if (!accounts) {
            return;
        }
        return accounts[activeAccountIndex];
    }, [activeAccountIndex, accounts]);

    /**
     * @fetchAccounts will get all accounts belonging to a user.
     * Every OAM user should have at least one account.
     * If an empty array is returned, then all user accounts are blocked - home page should display blocked account message.
     * If getAccounts request fails or nothing is returned, we throw an error and sign user out.
     **/

    const fetchAccounts = useCallback(async () => {
        atatus.startTransaction('GetAccounts');
        try {
            setAccountFetchingStatus(true);
            let acc = await getAccounts();

            if (!acc) {
                throw new Error('failed to get accounts');
            }

            setAccounts(acc);
            atatus.endTransaction('GetAccounts');
        } catch (e) {
            atatus.failTransaction('GetAccounts');
            signOut(AppRoutes.connectionFailurePage);
        } finally {
            setAccountFetchingStatus(false);
        }
        /**
         * Omitted @signOut from useEffect dependency array.
         * A change to the signOut function should not run this effect.
         */
    }, []);

    useEffect(() => {
        if (!user) {
            return;
        }
        fetchAccounts();
    }, [user, fetchAccounts]);

    const hasAmountsPastDue = useMemo(
        () => (accounts && accounts.length ? accounts.some((acc) => acc.amountsPastDue > 0) : true),
        [accounts]
    );

    return (
        <Context.Provider
            value={{
                accounts,
                activeAccount,
                activeAccountIndex,
                fetchAccounts,
                hasAmountsPastDue,
                isFetchingAccounts,
                setActiveAccountIndex,
            }}
        >
            {children}
        </Context.Provider>
    );
};

const useAccounts = () => useContext(Context);

export default useAccounts;
